/**
 * "Where do I get it?" links. Never an invented product URL: an Amazon search for the exact product name
 * (research/sanitizer-selection.md, Amazon section). The Associates tag comes from settings, never from code, and the
 * disclosure line is shown wherever a tagged link appears. The safety pick comes first; a commission never decides
 * which product is named.
 */
export const AMAZON_DISCLOSURE = "As an Amazon Associate I earn from qualifying purchases.";

export function amazonSearchUrl(product: string, tag?: string): string {
  const q = encodeURIComponent(product.replace(/\s+/g, " ").trim());
  return `https://www.amazon.com/s?k=${q}${tag ? `&tag=${encodeURIComponent(tag)}` : ""}`;
}

/* ─────────────── Specific searches for "You need" lines (owner, 2026-09-28) ─────────────── */

/**
 * "Amazon links to search with specificity for all these things." A You-need line is turned into searches that name
 * the exact thing: a brand our sources name ("Bona Wood Floor Cleaner") is searched as written; a vague item is made
 * specific from a fixed list and the guide's own object ("A stiff brush" in the grout guide → "stiff grout brush").
 * Lines you can't buy (care labels, instructions, water, a second person) get no link. Never an invented product.
 */
export interface BuySearch { label: string; query: string }

const NOT_BUYABLE = /\bcare label|\binstructions\b|\bmanual\b|\bsecond person\b|\blaundromat\b|^water\b|\bhot water\b|\byour hands\b|\bspare hand towels\b|\bthe care labels?\b/i;

/** Brands and product names our sources name (research rows); searched exactly as written. Longest first. */
const NAMED = [
  "Armstrong Flooring Once 'n Done", "Bona Premium Spray Mop", "Bona Hardwood Floor Cleaner", "Bona Wood Floor Cleaner",
  "Clorox Disinfecting Bleach", "Clorox Disinfecting Wipes", "Lysol Disinfectant Spray", "affresh Washing Machine Cleaner",
  "Bar Keepers Friend", "Spic and Span", "Mr. Clean", "Brasso", "Lodge Chainmail Scrubber", "Lodge Pan Scraper", "Lodge Seasoning Spray",
  "Cascade", "Finish Jet-Dry", "Steramine",
].sort((a, b) => b.length - a.length);

/** Vague → specific. `ctx` = put the guide's object in front ("{ctx} brush"). Most specific first. */
const SPECIFIC: [RegExp, string, boolean?][] = [
  [/\btoilet bowl cleaner\b/i, "EPA registered toilet bowl cleaner"],
  [/\btoilet brush\b/i, "long handled toilet brush with holder"],
  [/\bepa[- ]registered disinfectant\b|\bdisinfectant\b/i, "EPA registered disinfectant spray"],
  [/\bdisinfecting wipes\b/i, "EPA registered disinfecting wipes"],
  [/\bglass\b.*\bcleaner\b|\bcleaner\b.*\b(mirrors?|glass)\b/i, "glass and mirror cleaner"],
  [/\btub\/tile\/sink cleaner\b|\btub and tile cleaner\b/i, "tub and tile cleaner"],
  [/\bnonabrasive all-purpose cleaner\b|\ball-purpose cleaner\b/i, "nonabrasive all purpose cleaner"],
  [/\bmild abrasive cleanser\b|\bliquid cleanser\b/i, "liquid cleanser for bathrooms"],
  [/\balkaline cleaner\b/i, "alkaline grout cleaner"],
  [/\bmildew stain remover\b/i, "mildew stain remover"],
  [/\bsqueegee\b/i, "shower squeegee"],
  [/\bstiff brush\b/i, "stiff {ctx} brush", true],
  [/\bsoft cloth\b|\bcloth\b/i, "microfiber cleaning cloths"],
  [/\bold toothbrush\b|\btoothbrush\b/i, "small detail cleaning brush"],
  [/\bgloves\b/i, "reusable cleaning gloves"],
  [/\bhepa\b.*\bvacuum\b|\bvacuum\b.*\bhepa\b/i, "HEPA filter vacuum"],
  [/\bupholstery tool\b/i, "vacuum upholstery attachment"],
  [/\bhot-water extraction cleaner\b/i, "carpet hot water extraction cleaner"],
  [/\bmop\b.*\bbucket\b|\bsecond bucket\b/i, "mop and two bucket set"],
  [/\bairtight bags\b|\bairtight containers\b/i, "airtight clothing storage bags"],
  [/\bslim hangers\b/i, "slim velvet hangers"],
  [/\bpadded hangers\b/i, "padded hangers"],
  [/\bgentle detergent\b|\bfree and clear\b/i, "fragrance free gentle laundry detergent"],
  [/\blaundry detergent\b/i, "laundry detergent"],
  [/\bchlorine bleach\b|\bliquid household bleach\b|\bliquid chlorine bleach\b/i, "regular liquid chlorine bleach"],
  [/\boxygen-based washer cleaner\b/i, "oxygen washing machine cleaner"],
  [/\bbaking soda\b/i, "baking soda"],
  [/\bfloor cleaner\b/i, "{ctx} floor cleaner", true],
  [/\btest strips?\b/i, "{ctx} test strips", true],
  [/\bdrying mat\b|\bdish mat\b/i, "silicone dish drying mat"],
];

const VERBS = /^(clean|wash|load|dust|vacuum|mop|wipe|scrub|dry|store|fold|care for|make|set up|descale|sanitize|disinfect|replace|empty|change)\b\s*/i;
/** "Clean the shower glass" → "shower glass". */
export function objectOf(title: string): string {
  return title.replace(VERBS, "").replace(/^(the|a|an|your)\s+/i, "").replace(/[.:]$/, "").trim().toLowerCase();
}

/** Every specific search a You-need line calls for (one line can name several things). */
export function buySearches(line: string, context = ""): BuySearch[] {
  if (NOT_BUYABLE.test(line) && !NAMED.some((n) => line.includes(n))) return [];
  const found: (BuySearch & { at: number })[] = [];
  let rest = line;
  for (const n of NAMED) {
    const at = rest.indexOf(n);
    if (at >= 0) { found.push({ label: n, query: n, at }); rest = rest.split(n).join(" ".repeat(n.length)); }
  }
  const ctx = objectOf(context).split(/\s+/).slice(-2).join(" ");
  for (const [re, q, useCtx] of SPECIFIC) {
    const m = rest.match(re);
    if (m && m.index !== undefined) {
      const query = useCtx ? (ctx ? q.replace("{ctx}", ctx) : q.replace("{ctx} ", "")) : q;
      if (!found.some((o) => o.query === query)) found.push({ label: query.charAt(0).toUpperCase() + query.slice(1), query, at: m.index });
      rest = rest.replace(re, (x) => " ".repeat(x.length));
    }
  }
  return found.sort((a, b) => a.at - b.at).map(({ label, query }) => ({ label, query }));
}
